import { VirtualDevice, type SimulationContext } from './VirtualDevice';
import { Tank, Pump, Valve, LevelSensor } from './devices/fluid';
import { PIDControllerDevice } from './devices/pid-device';
import { ModbusServerDevice, ModbusClientDevice } from './devices/modbus';

// --- Input Devices ---

export class PushButton extends VirtualDevice {
  public override state = { pressed: false };

  override reset() {
    this.state.pressed = false;
  }

  override update(context: SimulationContext) {
    this.writePlcInput(context.host, this.state.pressed);
  }
}

export class EmergencyStop extends VirtualDevice {
  public override state = { triggered: false };

  override reset() {
    this.state.triggered = false;
  }

  override update(context: SimulationContext) {
    // E-Stops are usually Normally Closed, meaning they send TRUE when normal, and FALSE when triggered.
    // However, depending on simple requirements, we just write the triggered state or !triggered.
    // Let's write `!triggered` as TRUE in normal state.
    this.writePlcInput(context.host, !this.state.triggered);
  }
}

export class SelectorSwitch extends VirtualDevice {
  public override state = { on: false };

  override reset() {
    this.state.on = false;
  }

  override update(context: SimulationContext) {
    this.writePlcInput(context.host, this.state.on);
  }
}

// --- Output Devices ---

export class PilotLamp extends VirtualDevice {
  public override state = { on: false };

  override reset() {
    this.state.on = false;
  }

  override update(context: SimulationContext) {
    this.state.on = this.readPlcOutput(context.host);
  }
}

export class Buzzer extends VirtualDevice {
  public override state = { on: false };

  override reset() {
    this.state.on = false;
  }

  override update(context: SimulationContext) {
    this.state.on = this.readPlcOutput(context.host);
  }
}

export class Motor extends VirtualDevice {
  public override state: { running: boolean, speed: number, load: 'NORMAL' | 'OVERLOAD' } = { running: false, speed: 0, load: 'NORMAL' };

  override reset() {
    this.state.running = false;
    this.state.speed = 0;
  }

  override update(context: SimulationContext) {
    this.state.running = this.readPlcOutput(context.host);
    // Simple speed ramp up/down for visual simulation
    if (this.state.running) {
      if (this.state.speed < 100) this.state.speed += (100 / 1000) * context.deltaTimeMs;
      if (this.state.speed > 100) this.state.speed = 100;
    } else {
      if (this.state.speed > 0) this.state.speed -= (100 / 1000) * context.deltaTimeMs;
      if (this.state.speed < 0) this.state.speed = 0;
    }
    
    // Evaluate overload alarm
    context.alarms.evaluateCondition(
      this.state.load === 'OVERLOAD',
      this.id,
      'MOTOR_OVERLOAD',
      'Motor Overload',
      'Motor is drawing excessive current and tripped thermal protection.',
      'ERROR',
      context.simulationTimeMs
    );
  }
}

export class FanDevice extends VirtualDevice {
  public override state: { running: boolean; speed: number; load: 'NORMAL' | 'OVERLOAD' } = {
    running: false,
    speed: 0,
    load: 'NORMAL',
  };

  override reset() {
    this.state.running = false;
    this.state.speed = 0;
  }

  override update(context: SimulationContext) {
    this.state.running = this.readPlcOutput(context.host);
    const targetSpeed = Number(this.props['speed'] || 100);
    if (this.state.running) {
      if (this.state.speed < targetSpeed) this.state.speed += (targetSpeed / 800) * context.deltaTimeMs;
      if (this.state.speed > targetSpeed) this.state.speed = targetSpeed;
    } else {
      if (this.state.speed > 0) this.state.speed -= (targetSpeed / 800) * context.deltaTimeMs;
      if (this.state.speed < 0) this.state.speed = 0;
    }

    if (context.alarms) {
      context.alarms.evaluateCondition(
        this.state.load === 'OVERLOAD',
        this.id,
        'FAN_OVERLOAD',
        'Fan Overload',
        'Cooling fan motor tripped thermal protection.',
        'ERROR',
        context.simulationTimeMs
      );
    }
  }
}

// --- Sensors ---

export class ProximitySensor extends VirtualDevice {
  public override state = { detected: false };

  override reset() {
    this.state.detected = false;
  }

  override update(context: SimulationContext) {
    let detected = false;
    const sx = this.transform.position.x;
    const sy = this.transform.position.y;
    const range = Number(this.props['range']) || 80;
    const variant = (this.props['variant'] as string) || 'photoelectric';
    const logicType = (this.props['logicType'] as string) || 'NO';

    for (const p of context.process.products) {
      if (!p.active) continue;

      let inZone = false;
      if (variant === 'photoelectric') {
        // Laser beam is a vertical ray at X = sx + 22 from Y = sy + 38 to Y = sy + 42 + range
        const beamX = sx + 22;
        const effectiveRange = Math.max(40, range);
        const beamYMin = sy + 38;
        const beamYMax = sy + 42 + effectiveRange;

        // Box physically cuts the vertical laser beam
        const xHit = p.x <= beamX && (p.x + p.width) >= beamX;
        const yHit = (p.y + p.height) >= beamYMin && p.y <= beamYMax;
        if (xHit && yHit) {
          inZone = true;
        }
      } else if (variant === 'light_curtain') {
        // Multi-beam light curtain between TX (sx + 18) and RX (sx + 66)
        const curtainXMin = sx + 18;
        const curtainXMax = sx + 66;
        const effectiveRange = Math.max(40, range);
        const curtainYMin = sy + 16;
        const curtainYMax = sy + 16 + effectiveRange;

        const xHit = (p.x + p.width) >= curtainXMin && p.x <= curtainXMax;
        const yHit = (p.y + p.height) >= curtainYMin && p.y <= curtainYMax;
        if (xHit && yHit) {
          inZone = true;
        }
      } else {
        // Inductive or Capacitive proximity sensor (centered at sx + 22)
        const centerX = sx + 22;
        const radius = variant === 'inductive' ? 14 : 16;
        const effectiveRange = Math.max(20, Math.min(80, range));
        const fieldYMin = sy + 44;
        const fieldYMax = sy + 44 + effectiveRange;

        const xHit = (p.x + p.width) >= (centerX - radius) && p.x <= (centerX + radius);
        const yHit = (p.y + p.height) >= fieldYMin && p.y <= fieldYMax;
        if (xHit && yHit) {
          inZone = true;
        }
      }

      if (inZone) {
        detected = true;
        break;
      }
    }

    this.state.detected = detected;
    const outputSignal = logicType === 'NC' ? !detected : detected;
    this.writePlcInput(context.host, outputSignal);
  }
}

export class JamSensor extends VirtualDevice {
  public override state = { detected: false };

  override reset() {
    this.state.detected = false;
  }

  override update(context: SimulationContext) {
    const targetConvId = this.props['conveyorId'] as string;
    let detected = false;
    
    if (targetConvId) {
      const conv = context.process.getDevice(targetConvId) as Conveyor | undefined;
      if (conv && conv.type === 'conveyor') {
        detected = conv.state.jammed;
      }
    }

    this.state.detected = detected;
    this.writePlcInput(context.host, this.state.detected);
  }
}

export class Conveyor extends VirtualDevice {
  public override state = { running: false, speed: 0, jammed: false };

  override reset() {
    this.state.running = false;
    this.state.speed = 0;
    this.state.jammed = false;
  }

  override update(context: SimulationContext) {
    const motorId = this.props['motorId'] as string;
    let isRunning = false;
    if (motorId) {
      const motor = context.process.getDevice(motorId) as Motor | undefined;
      if (motor && motor.type === 'motor') {
        isRunning = motor.state.running;
      }
    }
    // Direct PLC output address on the conveyor's integrated drive motor
    if (!isRunning && this.props['address']) {
      isRunning = this.readPlcOutput(context.host);
    }

    this.state.running = isRunning;
    this.state.speed = isRunning ? Number(this.props['speed'] || 100) : 0;

    // Evaluate jammed alarm
    context.alarms?.evaluateCondition(
      this.state.jammed,
      this.id,
      'CONVEYOR_JAM',
      'Conveyor Jammed',
      'A product jam has been detected on the conveyor.',
      'ERROR',
      context.simulationTimeMs
    );
  }
}

export class MixerDevice extends VirtualDevice {
  public override state: { running: boolean; speed: number; load: 'NORMAL' | 'OVERLOAD' } = {
    running: false,
    speed: 0,
    load: 'NORMAL',
  };

  override reset() {
    this.state.running = false;
    this.state.speed = 0;
  }

  override update(context: SimulationContext) {
    this.state.running = this.readPlcOutput(context.host);
    const targetSpeed = Number(this.props['speed'] || 100);
    if (this.state.running) {
      if (this.state.speed < targetSpeed) this.state.speed += (targetSpeed / 800) * context.deltaTimeMs;
      if (this.state.speed > targetSpeed) this.state.speed = targetSpeed;
    } else {
      if (this.state.speed > 0) this.state.speed -= (targetSpeed / 800) * context.deltaTimeMs;
      if (this.state.speed < 0) this.state.speed = 0;
    }
  }
}

export const DEVICE_FACTORIES: Record<string, new (...args: any[]) => VirtualDevice<any>> = {
  'button': PushButton,
  'emergency-stop': EmergencyStop,
  'switch': SelectorSwitch,
  'lamp': PilotLamp,
  'buzzer': Buzzer,
  'motor': Motor,
  'fan': FanDevice,
  'mixer': MixerDevice,
  'sensor': ProximitySensor,
  'jam-sensor': JamSensor,
  'conveyor': Conveyor,
  'tank': Tank,
  'pump': Pump,
  'valve': Valve,
  'level-sensor': LevelSensor,
  'pid-controller': PIDControllerDevice,
  'modbus-server': ModbusServerDevice,
  'modbus-client': ModbusClientDevice,
};
